import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    Modal,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Paper,
    CircularProgress,
    Alert,
    Grid,
    Card
} from '@mui/material';
import {
    Close,
    CloudUpload
} from '@mui/icons-material';
import SuccessModal from './SuccessModal';
import { stateAxios, ticketAxios } from '../services/ticketAxios';
import { AppDispatch, RootState } from '@store/store';
import { createTicket } from '../redux/thunk/ticketThunk';
import { axiosInstanceHelpDesk } from '@config/axiosConfig';

interface IssueType {
    issueTypeID: number;
    name: string;
}

interface UserType {
    userTypeID: number;
    name: string;
}

interface State {
    id: number;
    state_lgd_code: number;
    state_or_ut: string;
    stateName: string;
}

interface District {
    id: number;
    stateID: number;
    district_lgd_code: number;
    districtName: string;
}

interface Organization {
    gst_number: string;
    cin: string;
    tin: string;
    business_name: string;
    business_nature: string | null;
    entity_type: string;
    district_name: string;
    pincode: number;
    state_name: string;
    city_name: string;
    registration_date: string;
}

interface ProfileData {
    id: number;
    name: string;
    email: string;
    mobile_number: string;
    gender: string;
    pan_number: string;
    dob: string;
    address: string;
    user_category: number;
    organizations?: Organization[];
}

interface CreateTicketPayload {
    mobile: string;
    name: string;
    email: string;
    stateName: string;
    districtName: string;
    userTypeID: string;
    issueTypeID: string;
    issueDetails: string;
    userID: number;
    attachment: any[];
}

const validationSchema = Yup.object({
    userTypeID: Yup.string().required('User type is required'),
    issueTypeID: Yup.string().required('Issue category is required'),
    issueDetails: Yup.string().required('Issue details are required').min(10, 'Issue details must be at least 10 characters'),
    stateName: Yup.string(),
    districtName: Yup.string(),
});

const CreateTicketModal = ({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    // Get profile data from Redux store
    const { profile } = useSelector((state: RootState) => state.profile);
    const profileData = profile as ProfileData | null;

    // Data lists
    const [issueTypes, setIssueTypes] = useState<IssueType[]>([]);
    const [userTypes, setUserTypes] = useState<UserType[]>([]);
    const [states, setStates] = useState<State[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);

    // UI states
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);

    const [ticketId, setTicketId] = useState<string>('');

    // Formik form
    const formik = useFormik({
        initialValues: {
            userTypeID: '',
            issueTypeID: '',
            issueDetails: '',
            stateName: '',
            districtName: '',
            selectedStateId: '',
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, setErrors, resetForm }) => {
            setSubmitting(true);

            const cleanMobileNumber = profileData?.mobile_number?.replace(/^\+91\s?/, '') || '';
            const payload = {
                // mobile: cleanMobileNumber,
                name: profileData?.name || '',
                email: profileData?.email || '',
                stateName: values.stateName,
                districtName: values.districtName,
                userTypeID: values.userTypeID,
                issueTypeID: values.issueTypeID,
                issueDetails: values.issueDetails,
                // userID: profileData?.id || 0,
                attachment: []
            };

            try {
                const resultAction = await dispatch(createTicket(payload));

                if (createTicket.fulfilled.match(resultAction)) {
                    const response = resultAction.payload;

                    if (response.success) {
                        setTicketId(response.data?.ticketNumber || '');
                        setOpenSuccessModal(true);
                        resetForm();
                        setAttachments([]);
                        onSuccess();

                        // Optional: Close the modal after a delay
                        setTimeout(() => {
                            onClose();
                        }, 100);
                    } else {
                        setErrors({
                            issueDetails: response.message || 'Failed to create ticket'
                        });
                    }
                } else if (createTicket.rejected.match(resultAction)) {
                    const error = resultAction.payload || resultAction.error;
                    setErrors({
                        issueDetails: (typeof error === 'object' && error !== null && 'message' in error)
                            ? (error as any).message
                            : 'Failed to create ticket'
                    });
                }
            } catch (error: any) {
                setErrors({
                    issueDetails: error?.message || 'An unexpected error occurred'
                });
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Fetch all required data when modal opens
    useEffect(() => {
        if (open) {
            fetchAllData();
        }
    }, [open]);

    // Fetch districts when state is selected
    useEffect(() => {
        if (formik.values.selectedStateId) {
            fetchDistricts(Number(formik.values.selectedStateId));
        } else {
            setDistricts([]);
            formik.setFieldValue('districtName', '');
        }
    }, [formik.values.selectedStateId]);

    const fetchAllData = async () => {
        try {
            setLoading(true);

            // Fetch all required data in parallel
            const [issueTypesRes, userTypesRes, statesRes] = await Promise.all([
                axiosInstanceHelpDesk.get('/getIssueType'),
                axiosInstanceHelpDesk.get('/getUserTypeList'),
                axiosInstanceHelpDesk.get('/state')
            ]);

            const issueTypesJson = issueTypesRes.data;
            const userTypesJson = userTypesRes.data;
            const statesJson = statesRes.data;

            if (issueTypesJson?.success) {
                setIssueTypes(issueTypesJson.data || []);
            }

            if (userTypesJson?.success) {
                setUserTypes(userTypesJson.data || []);
            }

            if (statesJson?.success) {
                // Filter out "All" state if needed
                const filteredStates = statesJson.data.filter((state: State) => state.id !== 1);
                setStates(filteredStates || []);
            }

        } catch (err: any) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDistricts = async (stateId: number) => {
        try {
            setLoadingDistricts(true);
            const response = await axiosInstanceHelpDesk.get(`/district?stateID=${stateId}`);

            if (response.data?.success) {
                setDistricts(response.data.data || []);
            }
        } catch (err: any) {
            console.error('Error fetching districts:', err);
            setDistricts([]);
        } finally {
            setLoadingDistricts(false);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const fileList = Array.from(files);
            setAttachments(prev => [...prev, ...fileList]); 
        }
    };

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleStateChange = (stateId: number) => {
        formik.setFieldValue('selectedStateId', stateId.toString());
        // Find and set state name
        const selectedState = states.find(state => state.id === stateId);
        formik.setFieldValue('stateName', selectedState?.stateName || '');
        // Reset district when state changes
        formik.setFieldValue('districtName', '');
    };

    const handleDistrictChange = (districtId: number) => {
        const selectedDistrict = districts.find(district => district.id === districtId);
        formik.setFieldValue('districtName', selectedDistrict?.districtName || '');
    };

    const handleClose = () => {
        formik.resetForm();
        setAttachments([]);
        onClose();
    };

    // Get user category text
    const getUserCategoryText = () => {
        if (!profileData) return '';
        return profileData.user_category === 2 ? 'Organisation' : 'Individual';
    };

    return (
        <>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="create-ticket-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Paper
                    sx={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        boxShadow: 24,
                        p: 0
                    }}
                >
                    <Box sx={{ overflow: 'auto', maxHeight: '80vh', }}>
                        {/* Header */}
                        <Box
                            sx={{
                                py: 1.5,
                                px: 3,
                                borderBottom: '1px solid #EEEBF7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                position: 'sticky',
                                top: 0,
                                bgcolor: '#fff',
                                zIndex: 1
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: '#1F2937'
                                }}
                            >
                                Create New Ticket
                            </Typography>
                            <IconButton
                                onClick={handleClose}
                                size="small"
                                disabled={formik.isSubmitting}
                                sx={{
                                    color: '#6B7280',
                                    '&:hover': {
                                        bgcolor: '#F3F4F6'
                                    }
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box sx={{ p: 3 }}>
                            <Typography
                                sx={{
                                    color: '#6B7280',
                                    fontSize: '0.875rem',
                                    mb: 1
                                }}
                            >
                                Fill out the form below to submit a new support ticket. Our team will respond within 24 hours.
                            </Typography>

                            <Card sx={{ mb: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }} variant='outlined'>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 500,
                                                mb: 0.5
                                            }}>
                                            Name
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                color: '#6B7280',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {profileData?.name || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 500,
                                                mb: 0.5
                                            }}>
                                            Mobile Number
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                color: '#6B7280',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {profileData?.mobile_number || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 500,
                                                mb: 0.5
                                            }}>
                                            Email Address
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                color: '#6B7280',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {profileData?.email || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                fontWeight: 500,
                                                mb: 0.5
                                            }}>
                                            User Type
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '1.125rem',
                                                color: '#6B7280',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {getUserCategoryText()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Card>

                            {/* Ticket Information Form */}
                            <form onSubmit={formik.handleSubmit}>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 600,
                                        color: '#1F2937',
                                        mb: 2
                                    }}
                                >
                                    Ticket Information
                                </Typography>

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 2 }}>
                                    {/* User Type Dropdown */}
                                    <FormControl fullWidth required error={formik.touched.userTypeID && Boolean(formik.errors.userTypeID)}>
                                        <InputLabel>User Type </InputLabel>
                                        <Select
                                            name="userTypeID"
                                            value={formik.values.userTypeID}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            label="User Type"
                                            disabled={loading || formik.isSubmitting}
                                            sx={{
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#E5E7EB'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3A1C73'
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ color: '#9CA3AF' }}>
                                                    Select User Type
                                                </Typography>
                                            </MenuItem>
                                            {userTypes.map((userType) => (
                                                <MenuItem
                                                    key={userType.userTypeID}
                                                    value={userType.userTypeID.toString()}
                                                >
                                                    {userType.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.userTypeID && formik.errors.userTypeID && (
                                            <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.5 }}>
                                                {formik.errors.userTypeID}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {/* Issue Category Dropdown */}
                                    <FormControl fullWidth required error={formik.touched.issueTypeID && Boolean(formik.errors.issueTypeID)}>
                                        <InputLabel>Issue Category</InputLabel>
                                        <Select
                                            name="issueTypeID"
                                            value={formik.values.issueTypeID}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            label="Issue Category"
                                            disabled={loading || formik.isSubmitting}
                                            sx={{
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#E5E7EB'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3A1C73'
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ color: '#9CA3AF' }}>
                                                    Select Category
                                                </Typography>
                                            </MenuItem>
                                            {issueTypes.map((issueType) => (
                                                <MenuItem
                                                    key={issueType.issueTypeID}
                                                    value={issueType.issueTypeID.toString()}
                                                >
                                                    {issueType.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.issueTypeID && formik.errors.issueTypeID && (
                                            <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.5 }}>
                                                {formik.errors.issueTypeID}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {/* State Dropdown */}
                                    <FormControl fullWidth>
                                        <InputLabel>State</InputLabel>
                                        <Select
                                            name="selectedStateId"
                                            value={formik.values.selectedStateId}
                                            onChange={(e) => handleStateChange(e.target.value as any)}
                                            label="State"
                                            disabled={loading || formik.isSubmitting || loadingStates}
                                            sx={{
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#E5E7EB'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3A1C73'
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ color: '#9CA3AF' }}>
                                                    Select State
                                                </Typography>
                                            </MenuItem>
                                            {states.map((state) => (
                                                <MenuItem
                                                    key={state.id}
                                                    value={state.id}
                                                >
                                                    {state.stateName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* District Dropdown */}
                                    <FormControl fullWidth>
                                        <InputLabel>District</InputLabel>
                                        <Select
                                            value={districts.find(d => d.districtName === formik.values.districtName)?.id || ''}
                                            onChange={(e) => handleDistrictChange(e.target.value as number)}
                                            label="District"
                                            disabled={loading || formik.isSubmitting || loadingDistricts || !formik.values.selectedStateId}
                                            sx={{
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#E5E7EB'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3A1C73'
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ color: '#9CA3AF' }}>
                                                    {loadingDistricts ? 'Loading districts...' : 'Select District'}
                                                </Typography>
                                            </MenuItem>
                                            {districts.map((district) => (
                                                <MenuItem
                                                    key={district.id}
                                                    value={district.id}
                                                >
                                                    {district.districtName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Issue Details Textarea */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '1.125rem',
                                            fontWeight: 500,
                                            color: '#1F2937',
                                            mb: 1
                                        }}
                                    >
                                        Issue Details
                                    </Typography>
                                    <TextField
                                        name="issueDetails"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="Please provide detailed information about your issue"
                                        value={formik.values.issueDetails}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={loading || formik.isSubmitting}
                                        error={formik.touched.issueDetails && Boolean(formik.errors.issueDetails)}
                                        helperText={formik.touched.issueDetails && formik.errors.issueDetails}
                                        required
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#E5E7EB'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#D1D5DB'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#3A1C73'
                                                }
                                            }
                                        }}
                                    />
                                </Box>

                                {/* File Upload */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: '#1F2937',
                                            mb: 1
                                        }}
                                    >
                                        Attachments (Optional)
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '2px dashed #D1D5DB',
                                            borderRadius: '8px',
                                            p: 3,
                                            textAlign: 'center',
                                            cursor: loading || formik.isSubmitting ? 'not-allowed' : 'pointer',
                                            opacity: loading || formik.isSubmitting ? 0.6 : 1,
                                            '&:hover': {
                                                borderColor: (loading || formik.isSubmitting) ? '#D1D5DB' : '#3A1C73',
                                                bgcolor: (loading || formik.isSubmitting) ? 'transparent' : '#F9FAFB'
                                            }
                                        }}
                                        onClick={() => {
                                            if (!loading && !formik.isSubmitting) {
                                                document.getElementById('file-upload')?.click();
                                            }
                                        }}
                                    >
                                        <CloudUpload
                                            sx={{
                                                fontSize: '40px',
                                                color: '#9CA3AF',
                                                mb: 1
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: '#3A1C73',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                mb: 0.5
                                            }}
                                        >
                                            Upload File
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: '#6B7280',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            Upload screenshots or relevant files (Max 10MB each)
                                        </Typography>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={handleFileUpload}
                                            disabled={loading || formik.isSubmitting}
                                        />
                                    </Box>

                                    {/* Display uploaded files */}
                                    {attachments.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    color: '#1F2937',
                                                    mb: 1
                                                }}
                                            >
                                                Uploaded Files:
                                            </Typography>
                                            {attachments.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 1,
                                                        mb: 1,
                                                        bgcolor: '#F9FAFB',
                                                        borderRadius: '4px',
                                                        border: '1px solid #E5E7EB'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            color: '#1F2937'
                                                        }}
                                                    >
                                                        {file.name}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeAttachment(index)}
                                                        disabled={formik.isSubmitting}
                                                        sx={{
                                                            color: '#EF4444',
                                                            '&:hover': {
                                                                bgcolor: '#FEE2E2'
                                                            }
                                                        }}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </form>
                        </Box>
                    </Box>
                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            p: 2,
                            borderTop: '1px solid #EEEBF7'
                        }}
                    >
                        <Button
                            type="button"
                            onClick={handleClose}
                            disabled={formik.isSubmitting}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                px: 3,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: '#6B7280',
                                border: '1px solid #D1D5DB',
                                '&:hover': {
                                    bgcolor: '#F9FAFB',
                                    borderColor: '#9CA3AF'
                                },
                                '&:disabled': {
                                    opacity: 0.5,
                                    cursor: 'not-allowed'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => formik.submitForm()}
                            variant="contained"
                            disabled={loading || formik.isSubmitting}
                            sx={{
                                bgcolor: '#3A1C73',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '8px',
                                px: 3,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#3E2490',
                                    boxShadow: 'none'
                                },
                                '&:disabled': {
                                    bgcolor: '#9CA3AF',
                                    cursor: 'not-allowed'
                                },
                                minWidth: '140px'
                            }}
                        >
                            {formik.isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : loading ? (
                                'Loading...'
                            ) : (
                                'Create Ticket'
                            )}
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* Success Modal - Show after creating ticket */}
            <SuccessModal
                open={openSuccessModal}
                onClose={() => setOpenSuccessModal(false)}
                ticketId={ticketId}
            />
        </>
    );
};

export default CreateTicketModal;